import React, { useState, useEffect } from 'react';
import { Modal, Card, Space, Typography, ColorPicker, Row, Col, Button, message, Alert, Tag } from 'antd';
import { BgColorsOutlined, ReloadOutlined, CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColorPickerProps } from 'antd';
import { useTheme, ThemeConfig } from '../contexts/ThemeContext';
import './Appearance.scss';

const { Text } = Typography;

const applicationNames: Record<string, string> = {
  app: 'Main App (ThiranAI)',
  training: 'LLM Training',
  demo: 'Demo Application',
  fallbackapp: 'Experimental App'
};

const applicationColors: Record<string, string> = {
  app: 'blue',
  training: 'green',
  demo: 'pink',
  fallbackapp: 'purple'
};

const applicationDefaultColors: Record<string, ThemeConfig> = {
  app: {
    primaryColor: '#13afe3',
    secondaryColor: '#6b7280'
  },
  training: {
    primaryColor: '#059669',
    secondaryColor: '#6b7280'
  },
  demo: {
    primaryColor: '#ec4899',
    secondaryColor: '#6b7280'
  },
  fallbackapp: {
    primaryColor: '#8b5cf6',
    secondaryColor: '#6b7280'
  }
};

interface AppearanceEditorProps {
  visible: boolean;
  onClose: () => void;
}

const AppearanceEditor: React.FC<AppearanceEditorProps> = ({ visible, onClose }) => {
  const { themeConfig, currentApplication, updateTheme } = useTheme();
  const [tempConfig, setTempConfig] = useState<ThemeConfig>(themeConfig);

  // Update temporary config when dialog opens
  useEffect(() => {
    if (visible) {
      setTempConfig(themeConfig);
    }
  }, [visible, themeConfig]);

  const handleColorChange = (key: keyof ThemeConfig, color: ColorPickerProps['value']) => {
    let colorString = '#13afe3';
    if (typeof color === 'string') {
      colorString = color;
    } else if (color && typeof color === 'object' && 'toHexString' in color) {
      colorString = (color as any).toHexString();
    }

    setTempConfig(prev => ({
      ...prev,
      [key]: colorString
    }));
  };

  const handleApply = () => {
    updateTheme(tempConfig);
    message.success(`Theme applied for ${applicationNames[currentApplication]}!`);
    onClose();
  };

  const handleReset = () => {
    const defaultConfig = applicationDefaultColors[currentApplication] || applicationDefaultColors.app;
    setTempConfig(defaultConfig);
    message.info(`Reset to default ${applicationNames[currentApplication]} theme`);
  };

  const handleCancel = () => {
    setTempConfig(themeConfig); // Reset to current theme
    onClose();
  };

  const presetColors = [
    '#13afe3', // ThiranAI Blue
    '#6366f1', // Indigo
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#10b981', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#3b82f6', // Blue
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#e11d48', // Rose
  ];

  const secondaryColors = [
    '#6b7280', // Gray-500 (default)
    '#374151', // Gray-700 (darker)
    '#9ca3af', // Gray-400 (lighter)
    '#4b5563', // Gray-600
    '#1f2937', // Gray-800
    '#111827', // Gray-900
  ];

  return (
    <Modal
      title="Appearance Editor"
      open={visible}
      onCancel={handleCancel}
      width={800}
      className="appearance-editor-modal"
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto',
          position: 'relative'
        }
      }}
      footer={[
        <Button key="reset" icon={<ReloadOutlined />} onClick={handleReset}>
          Reset to Default
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="apply" type="primary" icon={<CheckCircleOutlined />} onClick={handleApply}>
          Apply Changes
        </Button>,
      ]}
    >
      <div className="space-y-6">
        {/* Application Context Alert */}
        <Alert
          message={
            <span>
              Customizing theme for{' '}
              <Tag color={applicationColors[currentApplication]}>
                {applicationNames[currentApplication]}
              </Tag>
            </span>
          }
          description="Each application maintains its own independent theme. Changes will only affect this application."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
        />

        {/* Current Theme Preview */}
        <Card title="Theme Preview" size="small">
          <div className="flex items-center space-x-6 appearance-editor__preview-section">
            <div className="flex items-center space-x-3">
              <div 
                className="color-circle" 
                style={{ backgroundColor: tempConfig.primaryColor }}
              />
              <div className="color-info">
                <div className="color-label">Primary Color</div>
                <div className="color-value">{tempConfig.primaryColor}</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div 
                className="color-circle" 
                style={{ backgroundColor: tempConfig.secondaryColor }}
              />
              <div className="color-info">
                <div className="color-label">Secondary Color</div>
                <div className="color-value">{tempConfig.secondaryColor}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Color Customization */}
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Card 
              title={
                <Space>
                  <BgColorsOutlined />
                  Primary Color
                </Space>
              } 
              size="small"
            >
              <Space direction="vertical" className="appearance-editor__color-picker-section full-width">
                <Text type="secondary">
                  Used for buttons, links, and primary UI elements
                </Text>
                <ColorPicker
                  value={tempConfig.primaryColor}
                  onChange={(color) => handleColorChange('primaryColor', color)}
                  presets={[{ label: 'Recommended', colors: presetColors }]}
                  showText
                  size="large"
                  className="full-width"
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </Space>
            </Card>
          </Col>

          <Col span={12}>
            <Card 
              title={
                <Space>
                  <BgColorsOutlined />
                  Secondary Color
                </Space>
              } 
              size="small"
            >
              <Space direction="vertical" className="appearance-editor__color-picker-section full-width">
                <Text type="secondary">
                  Used for secondary text and subtle UI elements
                </Text>
                <ColorPicker
                  value={tempConfig.secondaryColor}
                  onChange={(color) => handleColorChange('secondaryColor', color)}
                  presets={[{ label: 'Gray Shades', colors: secondaryColors }]}
                  showText
                  size="large"
                  className="full-width"
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Live Preview */}
        <Card title="Live Preview" size="small">
          <div className="space-y-4 appearance-editor__live-preview">
            <div className="flex space-x-4">
              <button 
                className="preview-button"
                style={{ backgroundColor: tempConfig.primaryColor }}
              >
                Primary Button
              </button>
              <button className="preview-button--default">
                Default Button
              </button>
            </div>
            
            <div className="space-y-2">
              <div className="sample-heading">Sample Heading</div>
              <div className="secondary-text" style={{ color: tempConfig.secondaryColor }}>
                This is secondary text that will use your selected secondary color
              </div>
              <div className="sample-text">
                <span 
                  className="highlight-text" 
                  style={{ color: tempConfig.primaryColor }}
                >
                  Links and highlights
                </span>{' '}
                will use your primary color
              </div>
            </div>

            <div className="ui-sample">
              <div className="sample-title">Sample UI Components</div>
              <div className="flex items-center space-x-4">
                <div 
                  className="indicator-circle" 
                  style={{ backgroundColor: tempConfig.primaryColor }}
                />
                <div className="indicator-text" style={{ color: tempConfig.secondaryColor }}>
                  Active indicator and description text
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Modal>
  );
};

export default AppearanceEditor;